import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { formatDateSafe } from '@/utils/dateUtils';
import {
    getCollaboratorProfileHref,
    getInitials,
    getModalityLabel,
    getProfileLinkLabel,
    SELL_STATUS_LABELS,
    getRequestStatusMetadata,
    getReceiptPendingLabel,
} from '@/features/Vacations/vacations.definitions';
import { KIND_LABELS } from '../../approvals.definitions';
import { DECIDED_LIST_LABELS } from './approvalDecidedList.definitions';
import type { VacationDecidedRowProps } from './approvalDecidedList.types';

export function VacationDecidedRow({ approval }: VacationDecidedRowProps) {
    const { request } = approval.item;
    const status = getRequestStatusMetadata(request);
    const receiptPendingLabel = getReceiptPendingLabel(request);
    const note = request.managerApproval.note;
    const decidedAt = request.managerApproval.at?.toDate?.();
    const profileHref = request.collaboratorId
        ? getCollaboratorProfileHref(request.collaboratorId)
        : null;
    return (
        <li className="flex flex-wrap items-center gap-3 px-4 py-3">
            {profileHref ? (
                <Link
                    to={profileHref}
                    aria-label={getProfileLinkLabel(request.collaboratorName)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-raro-gray-600 transition-colors hover:bg-primary-100 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 font-montserrat"
                >
                    {getInitials(request.collaboratorName)}
                </Link>
            ) : (
                <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-raro-gray-600 font-montserrat"
                    aria-hidden="true"
                >
                    {getInitials(request.collaboratorName)}
                </span>
            )}
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-raro-gray-900 font-montserrat">
                    {profileHref ? (
                        <Link
                            to={profileHref}
                            className="rounded transition-colors hover:text-primary-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                        >
                            {request.collaboratorName}
                        </Link>
                    ) : (
                        request.collaboratorName
                    )}
                </p>
                <p className="truncate text-xs text-raro-gray-500 font-montserrat">
                    {KIND_LABELS.vacation} · {getModalityLabel(request.modality)} ·{' '}
                    {request.segments
                        .map(
                            (segment) =>
                                `${formatDateSafe(segment.startDate)} a ${formatDateSafe(
                                    segment.endDate
                                )}`
                        )
                        .join(' · ')}
                </p>
                {note && (
                    <p className="mt-1 text-xs text-raro-gray-600 font-montserrat">
                        <span className="font-medium">{DECIDED_LIST_LABELS.noteLabel}:</span> {note}
                    </p>
                )}
                {request.sellRequest.requested && request.status !== 'Rejected' && (
                    <p className="mt-1 text-xs text-raro-gray-600 font-montserrat">
                        {SELL_STATUS_LABELS[request.sellRequest.status]}
                    </p>
                )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
                <div className="flex items-center gap-1">
                    <span
                        className={clsx(
                            'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold font-montserrat',
                            status.className
                        )}
                    >
                        {status.label}
                    </span>
                    {receiptPendingLabel && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800 font-montserrat">
                            {receiptPendingLabel}
                        </span>
                    )}
                </div>
                {decidedAt && (
                    <span className="text-[11px] text-raro-gray-400 font-montserrat">
                        {DECIDED_LIST_LABELS.decidedAt} {formatDateSafe(decidedAt)}
                    </span>
                )}
            </div>
        </li>
    );
}
